import { useMutation, useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';
import type {
  DiscoveryEvent,
  DiscoveryPreviewResponse,
  DiscoveryRun,
  ListDiscoveryRunsResponse,
  ListSupplierCandidatesResponse,
  PromoteCandidateRequest,
  PromoteCandidateResponse,
  SupplierCandidate,
} from '@sourcito/schemas';
import { apiFetch } from './api';

// ─── Preview (cheap pre-flight, runs only the LLM query generator) ─────

export async function previewDiscovery(asin: string): Promise<DiscoveryPreviewResponse> {
  return apiFetch<DiscoveryPreviewResponse>(`/api/products/${asin}/discover/preview`, {
    method: 'POST',
    body: '{}',
  });
}

// ─── SSE stream consumer for POST /api/products/:asin/discover ───────────
//
// Mirrors the streamNlSearch pattern: fetch + ReadableStream + manual SSE
// block parsing. EventSource isn't usable here because we POST and want
// cookie auth; EventSource is GET-only without a session-cookie header API.

export async function* streamDiscovery(
  asin: string,
  options: { signal?: AbortSignal } = {}
): AsyncGenerator<DiscoveryEvent> {
  const res = await fetch(`/api/products/${asin}/discover`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
    signal: options.signal,
  });

  if (!res.ok) {
    let message = `discovery returned HTTP ${res.status}`;
    try {
      const err = (await res.json()) as { error?: { message?: string } };
      if (err.error?.message) message = err.error.message;
    } catch {
      /* not JSON */
    }
    throw new Error(message);
  }
  if (!res.body) throw new Error('Discovery response had no body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let blockEnd: number;
      while ((blockEnd = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, blockEnd);
        buffer = buffer.slice(blockEnd + 2);
        const parsed = parseSSEBlock(block);
        if (parsed) yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSSEBlock(block: string): DiscoveryEvent | null {
  let data: string | null = null;
  for (const line of block.split('\n')) {
    if (line.startsWith('data:')) data = line.slice(5).trim();
  }
  if (data === null) return null;
  try {
    return JSON.parse(data) as DiscoveryEvent;
  } catch {
    return null;
  }
}

// ─── Candidates query / mutations ────────────────────────────────────────

export function candidatesQuery(asin: string, status?: string) {
  return queryOptions({
    queryKey: ['supplier-candidates', asin, status ?? 'any'] as const,
    queryFn: () => {
      const search = new URLSearchParams({ asin });
      if (status) search.set('status', status);
      return apiFetch<ListSupplierCandidatesResponse>(
        `/api/supplier-candidates?${search.toString()}`
      );
    },
    // Callers with no ASIN yet (e.g. a dossier whose brand has no catalog
    // rows) would otherwise fire a guaranteed 400 — asin is zod-validated
    // server-side as exactly 10 chars.
    enabled: Boolean(asin),
    staleTime: 5_000,
  });
}

export function useCandidates(asin: string, status?: string) {
  return useQuery(candidatesQuery(asin, status));
}

export function usePromoteCandidate(asin: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; overrides?: PromoteCandidateRequest }) =>
      apiFetch<PromoteCandidateResponse>(`/api/supplier-candidates/${input.id}/promote`, {
        method: 'POST',
        body: JSON.stringify(input.overrides ?? {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-candidates', asin] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['suppliers-for-asin', asin] });
    },
  });
}

export function useDismissCandidate(asin: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<SupplierCandidate>(`/api/supplier-candidates/${id}/dismiss`, {
        method: 'POST',
        body: '{}',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplier-candidates', asin] });
    },
  });
}

// ─── Discovery runs query ────────────────────────────────────────────────

export function discoveryRunsQuery(asin: string) {
  return queryOptions({
    queryKey: ['discovery-runs', asin] as const,
    queryFn: () =>
      apiFetch<ListDiscoveryRunsResponse>(`/api/discovery-runs?asin=${encodeURIComponent(asin)}`),
    staleTime: 10_000,
  });
}

export function useDiscoveryRuns(asin: string) {
  return useQuery(discoveryRunsQuery(asin));
}

export function useInvalidateDiscoveryRuns(asin: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['discovery-runs', asin] });
    qc.invalidateQueries({ queryKey: ['supplier-candidates', asin] });
  };
}

// ─── Helper: derive a friendly progress label from an event ──────────────

export function eventProgressLabel(event: DiscoveryEvent): string {
  switch (event.type) {
    case 'run_started':
      return 'Starting discovery…';
    case 'queries_generated':
      return `Generated ${event.queries.length} search ${event.queries.length === 1 ? 'query' : 'queries'}`;
    case 'searched':
      return `Searched: ${event.query} (${event.resultCount} ${event.resultCount === 1 ? 'result' : 'results'})`;
    case 'parsing':
      return `Parsing ${event.totalResults} unique ${event.totalResults === 1 ? 'result' : 'results'}`;
    case 'candidates_found':
      return `Found ${event.candidates.length} ${event.candidates.length === 1 ? 'candidate' : 'candidates'}`;
    case 'run_completed':
      return `Run completed (${event.candidateCount} ${event.candidateCount === 1 ? 'candidate' : 'candidates'})`;
    case 'run_failed':
      return `Run failed: ${event.error}`;
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

export type { DiscoveryEvent, DiscoveryRun, SupplierCandidate };
